import { parse as parseToml } from "smol-toml";

export type DependencySource =
  | "package.json"
  | "requirements.txt"
  | "pyproject.toml"
  | "Dockerfile";

export const DEPENDENCY_SOURCES: DependencySource[] = [
  "package.json",
  "requirements.txt",
  "pyproject.toml",
  "Dockerfile",
];

export type StepDependency = {
  name: string;
  version: string | null;
  source: DependencySource;
};

type ExtractedDependency = { name: string; version: string | null };

function extractDepsFromPackageJson(content: string): ExtractedDependency[] {
  try {
    const pkg = JSON.parse(content);
    const deps: ExtractedDependency[] = [];
    const collect = (value: unknown) => {
      if (typeof value !== "object" || value === null || Array.isArray(value))
        return;
      for (const [name, version] of Object.entries(value)) {
        if (typeof version === "string") deps.push({ name, version });
      }
    };
    collect(pkg.dependencies);
    collect(pkg.devDependencies);
    return deps.sort((a, b) => a.name.localeCompare(b.name));
  } catch {
    return [];
  }
}

// Parses a PEP 508 requirement ("requests[security]>=2.31 ; python_version >
// '3.8'", "torch @ git+https://…") into a name and version specifier. Returns
// null for option lines, bare URLs, and local paths.
function parseRequirementLine(line: string): ExtractedDependency | null {
  const requirement = line.split("#")[0]!.split(";")[0]!.trim();
  if (!requirement || requirement.startsWith("-")) return null;
  const match = requirement.match(/^([A-Za-z0-9][A-Za-z0-9._-]*)(.*)$/);
  if (!match) return null;
  const rest = match[2]!.replace(/^\[[^\]]*\]/, "").trim();
  if (rest.startsWith("@")) return { name: match[1]!, version: null };
  if (rest && !/^[<>=!~]/.test(rest)) return null;
  return { name: match[1]!, version: rest || null };
}

function extractDepsFromRequirementsTxt(
  content: string
): ExtractedDependency[] {
  const deps: ExtractedDependency[] = [];
  for (const line of content.split("\n")) {
    const parsed = parseRequirementLine(line);
    if (parsed) deps.push(parsed);
  }
  return deps;
}

function extractDepsFromPyprojectToml(content: string): ExtractedDependency[] {
  try {
    const doc = parseToml(content) as {
      project?: {
        dependencies?: unknown;
        "optional-dependencies"?: unknown;
      };
    };
    const deps: ExtractedDependency[] = [];
    const collect = (requirements: unknown) => {
      if (!Array.isArray(requirements)) return;
      for (const requirement of requirements) {
        if (typeof requirement !== "string") continue;
        const parsed = parseRequirementLine(requirement);
        if (parsed) deps.push(parsed);
      }
    };
    collect(doc.project?.dependencies);
    const groups = doc.project?.["optional-dependencies"];
    if (typeof groups === "object" && groups !== null) {
      for (const list of Object.values(groups)) collect(list);
    }
    return deps;
  } catch {
    return [];
  }
}

// Base images from every build stage. Flags (--platform), unresolved
// ${variables}, references to earlier stage aliases, and the 3b/base and
// scratch pseudo-images are all skipped.
function extractBaseImages(content: string): string[] {
  const images: string[] = [];
  const aliases = new Set<string>();
  for (const match of content.matchAll(/^\s*FROM\s+(.+)$/gim)) {
    const tokens = match[1]!.trim().split(/\s+/);
    const image = tokens.find((t) => !t.startsWith("--"));
    const asIndex = tokens.findIndex((t) => t.toLowerCase() === "as");
    const alias = asIndex >= 0 ? tokens[asIndex + 1] : undefined;
    const isAliasRef =
      image !== undefined &&
      image !== alias &&
      aliases.has(image.toLowerCase());
    if (alias) aliases.add(alias.toLowerCase());
    if (!image || image.includes("$") || isAliasRef) continue;
    if (image === "3b/base" || image === "scratch") continue;
    images.push(image);
  }
  return images;
}

const MANIFEST_EXTRACTORS: Record<
  string,
  (content: string) => ExtractedDependency[]
> = {
  "package.json": extractDepsFromPackageJson,
  "requirements.txt": extractDepsFromRequirementsTxt,
  "pyproject.toml": extractDepsFromPyprojectToml,
  Dockerfile: (content) =>
    extractBaseImages(content).map((name) => ({ name, version: null })),
};

export function isDependencySourceFile(path: string): boolean {
  return path.split("/").pop()! in MANIFEST_EXTRACTORS;
}

export function extractStepDependencies(
  files: Iterable<{ path: string; content: string }>
): StepDependency[] {
  const deps: StepDependency[] = [];
  const seen = new Set<string>();

  for (const file of files) {
    const fileName = file.path.split("/").pop()!;
    const extract = MANIFEST_EXTRACTORS[fileName];
    if (!extract) continue;
    const source = fileName as DependencySource;
    for (const dep of extract(file.content)) {
      const key = `${source}:${dep.name}`;
      if (seen.has(key)) continue;
      seen.add(key);
      deps.push({ name: dep.name, version: dep.version, source });
    }
  }

  return deps;
}
